import { createContext, ReactNode, useContext } from 'react';

import { HoverCard, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

import { CHECKLIST_SECTION_LABEL } from '@/utils/enrollmentChecklist';
import { usePatientEnrollmentStatus } from '@/hooks/usePatientEnrollmentStatus';

import type { PatientEnrollmentStatus } from '@/hooks/usePatientEnrollmentStatus';
import type {
  ChecklistItem,
  ChecklistSection,
  ChecklistSectionKey,
  EnrollmentChecklistResult
} from '@/utils/enrollmentChecklist';

const EMPTY_STATUS: PatientEnrollmentStatus = { loading: false, error: false, evaluation: null };

const EnrollmentStatusContext = createContext<PatientEnrollmentStatus>(EMPTY_STATUS);

/** Runs the read-only enrollment checklist once for the patient detail page. */
export const EnrollmentStatusProvider = ({
  patientId,
  children
}: {
  patientId: string;
  children: ReactNode;
}) => {
  const status = usePatientEnrollmentStatus(patientId, true);
  return (
    <EnrollmentStatusContext.Provider value={status}>{children}</EnrollmentStatusContext.Provider>
  );
};

/** The evaluation, or null while loading, on error, or outside the provider. */
function useEvaluation(): EnrollmentChecklistResult | null {
  const { loading, error, evaluation } = useContext(EnrollmentStatusContext);
  if (loading || error) return null;
  return evaluation;
}

function missingItems(section: ChecklistSection): ChecklistItem[] {
  return section.items.filter((item) => item.required && !item.complete);
}

/** POC yellow. A small alert; the hover lists what is missing. */
const POC_YELLOW = '#d9a206';

const GapAlert = ({ label, children }: { label: string; children: ReactNode }) => (
  <HoverCard width={280} shadow="md" withArrow openDelay={100} position="bottom-start">
    <HoverCard.Target>
      <span
        role="img"
        tabIndex={0}
        aria-label={label}
        style={{ display: 'inline-flex', cursor: 'help', flexShrink: 0, lineHeight: 0 }}>
        <IconAlertTriangle size={16} color={POC_YELLOW} stroke={2} />
      </span>
    </HoverCard.Target>
    <HoverCard.Dropdown>{children}</HoverCard.Dropdown>
  </HoverCard>
);

interface EnrollmentGapIndicatorProps {
  /** Section to show. Read from the page's enrollment status when `result` is not given. */
  section?: ChecklistSectionKey;
  result?: ChecklistSection | null;
}

/** Yellow alert for one checklist section. Nothing when complete or not required. */
const EnrollmentGapIndicator = ({ section, result }: EnrollmentGapIndicatorProps) => {
  const evaluation = useEvaluation();
  const row = result ?? evaluation?.sections.find((entry) => entry.key === section) ?? null;
  if (!row || row.status !== 'incomplete') return null;
  const items = missingItems(row);
  const count = row.missingCount || items.length;
  if (count === 0) return null;

  return (
    <GapAlert label={`${row.label}: ${count} missing`}>
      <Text size="xs" fw={600} mb={6}>
        {row.label}: missing for enrollment
      </Text>
      <Stack gap={6}>
        {items.map((item) => (
          <div key={item.key}>
            <Text size="xs" fw={600}>
              {item.label}
            </Text>
            {item.reason && (
              <Text size="xs" c="dimmed">
                {item.reason}
              </Text>
            )}
          </div>
        ))}
      </Stack>
    </GapAlert>
  );
};

/** "Enrollment: N missing" summary for the patient header, grouped by section. */
export const EnrollmentGapSummary = () => {
  const evaluation = useEvaluation();
  if (!evaluation || evaluation.isComplete || evaluation.missing.length === 0) return null;

  const groups = evaluation.sections
    .map((row) => ({
      key: row.key,
      labels: evaluation.missing
        .filter((entry) => entry.sectionKey === row.key)
        .map((entry) => entry.label)
    }))
    .filter((group) => group.labels.length > 0);

  return (
    <GapAlert label={`Enrollment: ${evaluation.missing.length} missing`}>
      <Text size="xs" fw={600} mb={6}>
        Missing for enrollment
      </Text>
      <Stack gap={6}>
        {groups.map((group) => (
          <div key={group.key}>
            <Text size="xs" fw={600}>
              {CHECKLIST_SECTION_LABEL[group.key]}
            </Text>
            <Text size="xs" c="dimmed">
              {group.labels.join(', ')}
            </Text>
          </div>
        ))}
      </Stack>
    </GapAlert>
  );
};

export default EnrollmentGapIndicator;
